import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TID } from "@/lib/testIds";

const COLUMNS = [
  { key: "paid", label: "New", colour: "var(--red)" },
  { key: "preparing", label: "Preparing", colour: "var(--yellow)" },
  { key: "ready_for_pickup", label: "Ready", colour: "var(--green)" },
  { key: "assigned", label: "Assigned", colour: "var(--blue)" },
  { key: "picked_up", label: "Picked up", colour: "var(--blue)" },
  { key: "delivering", label: "Delivering", colour: "var(--pink)" },
  { key: "delivered", label: "Delivered", colour: "var(--green)" },
];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);

  const load = async () => {
    const { data } = await api.get("/store/orders");
    setOrders(data);
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, []);

  const advance = async (orderId, status) => {
    try {
      await api.post(`/orders/${orderId}/status`, { status });
      toast.success(`Moved to ${status.replace("_", " ")}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div data-testid={TID.storeAdminDashboard} className="space-y-6">
      <div>
        <span className="label-eyebrow">Orders inbox</span>
        <h1 className="mt-1 font-display text-4xl">
          Live orders ({orders.filter((o) => o.status !== "delivered" && o.status !== "cancelled").length})
        </h1>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 overflow-x-auto">
        {COLUMNS.map((col) => {
          const list = orders.filter((o) => o.status === col.key);
          return (
            <div key={col.key} className="min-w-[220px]">
              <div className="flex items-center justify-between mb-3">
                <span
                  className="px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-widest text-white border-2 border-[var(--text)]"
                  style={{ background: col.colour, boxShadow: "2px 2px 0 0 var(--text)" }}
                >
                  {col.label}
                </span>
                <span className="text-xs font-black text-[var(--text-2)]">{list.length}</span>
              </div>
              <div className="space-y-2 min-h-[100px]">
                {list.map((o) => (
                  <div
                    key={o.order_id}
                    data-testid={TID.storeOrderCard}
                    className="panel-admin p-3"
                  >
                    <p className="text-[10px] font-black text-[var(--text-3)] tracking-widest uppercase">
                      #{o.order_id.slice(-6).toUpperCase()}
                    </p>
                    <p className="mt-1 text-sm font-black" style={{ fontFamily: "'Fraunces', serif" }}>
                      {o.items.reduce((n, it) => n + it.quantity, 0)} items · R{o.total.toFixed(2)}
                    </p>
                    <p className="mt-1 text-[11px] text-[var(--text-2)] line-clamp-2">
                      {o.delivery_address}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {o.status === "paid" && (
                        <button
                          data-testid={TID.storePrepBtn}
                          onClick={() => advance(o.order_id, "preparing")}
                          className="text-[11px] px-2 py-1 rounded-full border-2 border-[var(--text)] bg-[var(--yellow)] font-bold hover:bg-[var(--yellow-dark)]"
                        >
                          Start prep
                        </button>
                      )}
                      {o.status === "preparing" && (
                        <button
                          data-testid={TID.storeReadyBtn}
                          onClick={() => advance(o.order_id, "ready_for_pickup")}
                          className="text-[11px] px-2 py-1 rounded-full border-2 border-[var(--text)] bg-[var(--green)] text-white font-bold hover:bg-[var(--green-dark)]"
                        >
                          Mark ready
                        </button>
                      )}
                      {["paid", "preparing", "ready_for_pickup"].includes(o.status) && (
                        <button
                          onClick={() => advance(o.order_id, "cancelled")}
                          className="text-[11px] px-2 py-1 rounded-full border-2 border-[var(--text)] bg-white text-[var(--text-2)] hover:bg-[var(--red)] hover:text-white"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
